/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OutOfServiceSecondaryReasonProps {
  id: string;
  name: string;
  outOfServiceReasonId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OutOfServiceSecondaryReasonRecord extends Record<OutOfServiceSecondaryReasonProps>({
  id: null as any,
  name: null as any,
  outOfServiceReasonId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    outOfServiceReasonId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OutOfServiceSecondaryReasonRecord {
    if (json == null) {
      throw new Error('OutOfServiceSecondaryReasonRecord.fromJS: parameter cannot be null');
    }
    return new OutOfServiceSecondaryReasonRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceSecondaryReasonJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'OutOfServiceSecondaryReasonJson.name'),
      outOfServiceReasonId: getFromOrThrow(
        json,
        (j) =>
          propValueOrDefault(j, 'outOfServiceReasonId', '00000000-0000-0000-0000-000000000000'),
        'OutOfServiceSecondaryReasonJson.outOfServiceReasonId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    outOfServiceReasonId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OutOfServiceSecondaryReasonRecord, Error[]] {
    if (json == null) {
      throw new Error('OutOfServiceSecondaryReasonRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OutOfServiceSecondaryReasonRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceSecondaryReasonJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'OutOfServiceSecondaryReasonJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      outOfServiceReasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'outOfServiceReasonId', '00000000-0000-0000-0000-000000000000'),
            'OutOfServiceSecondaryReasonJson.outOfServiceReasonId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OutOfServiceSecondaryReasonProps) {
    super(props);
    if (props == null) {
      throw new Error('OutOfServiceSecondaryReasonRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OutOfServiceSecondaryReasonViewModelBase
  implements OutOfServiceSecondaryReasonProps
{
  protected readonly _props: OutOfServiceSecondaryReasonProps;
  private _json?: OutOfServiceSecondaryReasonProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get outOfServiceReasonId(): string {
    return this._props.outOfServiceReasonId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: OutOfServiceSecondaryReasonProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        outOfServiceReasonId: this._props.outOfServiceReasonId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOutOfServiceSecondaryReasonEntities = (state: AppState) =>
  state.entityStores.outOfServiceSecondaryReason.entities;

export const selectOutOfServiceSecondaryReasonById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceSecondaryReason.entities.get(id);

export const selectOutOfServiceSecondaryReasonMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.outOfServiceSecondaryReason.meta.get(id);

export const selectOutOfServiceSecondaryReasonAndMetadataById = (id: string) =>
  createSelector(
    selectOutOfServiceSecondaryReasonById(id),
    selectOutOfServiceSecondaryReasonMetadataById(id),
    (outOfServiceSecondaryReason, meta) => ({ outOfServiceSecondaryReason, meta }),
  );

export function selectOutOfServiceSecondaryReasonEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOutOfServiceSecondaryReasonEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OutOfServiceSecondaryReasonRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOutOfServiceSecondaryReasonEntitiesByName(name: string) {
  return createSelector(selectAllOutOfServiceSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectOutOfServiceSecondaryReasonEntitiesByOutOfServiceReasonId(
  outOfServiceReasonId: string,
) {
  return createSelector(selectAllOutOfServiceSecondaryReasonEntities, (entities) =>
    entities.filter((e) => e.outOfServiceReasonId === outOfServiceReasonId).valueSeq(),
  );
}
//#endregion Selectors
